/**
 * Audio Sidecar contract (PLAT-11696, Faster TTFE Leg 3).
 *
 * Glossary: the *Original* is the file exactly as the creator uploaded it; the
 * *Audio Sidecar* is its audio track demuxed on the creator's device — same
 * audio bytes, no re-encode — and uploaded ahead of it so the audio work can
 * start before the Original lands (the *Sidecar Path*). Whenever a Sidecar
 * cannot be produced the upload silently takes today's *Server-Extract Path*;
 * the Sidecar is an accelerator and never blocks the outcome.
 */

/** Mirror of the server Timing Gate: audio must not start later than video + 5 ms. */
export interface TimingGate {
  audioStartMs: number;
  videoStartMs: number;
  pass: boolean;
}

/**
 * Identity of the audio stream, as ffprobe would report it on the Original.
 * Recorded so the server can later confirm the Sidecar came from this Original.
 */
export interface SidecarIdentity {
  sample_rate: number;
  /** Sum of sample durations, in media timescale units. */
  duration_ts: number;
  nb_frames: number;
  /** Seconds, after the edit list — ffprobe's `start_time`. */
  start_time: number;
}

export interface SidecarResult {
  /** Flat M4A (`ftyp M4A `, `moov`, one `mdat`), `audio/mp4`. */
  blob: Blob;
  gate: TimingGate;
  audioDurationMs: number;
  identity: SidecarIdentity;
  /** Sample entry fourcc / codec string, e.g. `mp4a.40.2`, `alac`, `Opus`. */
  codec: string;
  /** Wall time spent demuxing (parse + copy), ms. */
  demuxMs: number;
  sidecarBytes: number;
  originalBytes: number;
}

/**
 * Why no Sidecar was produced. Every reason means "take the Server-Extract
 * Path"; the reason is only recorded for telemetry, never shown to the user.
 *
 * Demux-side reasons come from `demuxAudioSidecar`; the last group is emitted
 * by the upload orchestration (PLAT-11697) when a good Sidecar is abandoned.
 */
export type SidecarSkipReason =
  // container / track shape
  | 'non-isobmff' // not MP4/QuickTime, or no moov reachable
  | 'no-audio-track' // no `soun` handler track
  | 'pcm-audio' // uncompressed audio: one sample per frame, heap bomb
  | 'encrypted' // enca / pssh — the server could not decode it anyway
  | 'fragmented' // mvex/moof present; the flat writer needs a second pass
  | 'co64-over-4gib' // 64-bit chunk offsets beyond 4 GiB: unverified (dev override lifts it)
  | 'unsupported-codec' // not in the server's Separate Audio whitelist (e.g. ALAC, FLAC)
  | 'complex-elst' // edit list not `[empty…][one media edit]`, rate ≠ 1, or cslg
  // timing gate
  | 'gate-fail' // audio starts > 5 ms after video
  | 'gate-unknown' // no video track / no samples: cannot evaluate the gate
  // operational
  | 'worker-unavailable'
  | 'worker-crashed'
  | 'timeout'
  | 'aborted'
  | 'oversize-sidecar' // sidecar > 25 % of the Original: something went wrong
  | 'unknown-error'
  // orchestration (PLAT-11697)
  | 'video-ahead' // video upload passed 50 % before the Sidecar was ready
  | 'original-landed-first' // the Original landed before the Sidecar did
  | 'sidecar-upload-failed'; // PUT failed after the silent retries

export interface SidecarSkip {
  result: null;
  skipReason: SidecarSkipReason;
  /** Free-form technical detail for logs (never shown to the user). */
  detail?: string;
  /** Present when the gate was evaluated (e.g. `gate-fail`). */
  gate?: TimingGate;
  demuxMs?: number;
}

export interface SidecarSuccess {
  result: SidecarResult;
  skipReason?: undefined;
  detail?: undefined;
  gate?: undefined;
  demuxMs?: undefined;
}

/** `result === null` means: take the Server-Extract Path. */
export type SidecarOutcome = SidecarSuccess | SidecarSkip;

export interface DemuxOptions {
  /** Bytes per `File.slice` read. 8 MB measured fastest; O(chunk) memory. */
  chunkBytes?: number;
  /** Reject a Sidecar larger than this fraction of the Original. Default 0.25. */
  maxSidecarRatio?: number;
  /**
   * Lift the `co64-over-4gib` skip (decision D). Set from the dev override so
   * the > 4 GiB demo fixture (decision L) can take the Sidecar Path for the
   * measurement run; see the note in `demuxAudioSidecar.ts`.
   */
  allowCo64Over4GiB?: boolean;
  signal?: AbortSignal;
}

export function skip(
  skipReason: SidecarSkipReason,
  extra: Omit<SidecarSkip, 'result' | 'skipReason'> = {},
): SidecarSkip {
  return { result: null, skipReason, ...extra };
}
